import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject } from 'rxjs';

export type ThemeMode = 'auto' | 'dark' | 'light';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private themeModeSubject = new BehaviorSubject<ThemeMode>('auto');
  private darkModeSubject = new BehaviorSubject<boolean>(false);

  public themeMode$ = this.themeModeSubject.asObservable();
  public darkMode$ = this.darkModeSubject.asObservable();

  private mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

  constructor(@Inject(DOCUMENT) private document: Document) {
    this.initializeTheme();
    this.watchSystemTheme();

    // Subscribe to theme changes and update HTML element
    this.darkMode$.subscribe(isDark => {
      this.updateHtmlTheme(isDark);
    });
  }

  private initializeTheme(): void {
    // Check if user has a saved preference
    const savedTheme = localStorage.getItem('theme') as ThemeMode;

    if (savedTheme && ['auto', 'dark', 'light'].includes(savedTheme)) {
      this.themeModeSubject.next(savedTheme);
      this.updateDarkMode(savedTheme);
    } else {
      // Default to auto mode
      this.themeModeSubject.next('auto');
      this.updateDarkMode('auto');
    }
  }

  private watchSystemTheme(): void {
    // Listen for changes to system theme
    this.mediaQuery.addEventListener('change', (e) => {
      // Only update if we're in auto mode
      if (this.themeModeSubject.value === 'auto') {
        this.darkModeSubject.next(e.matches);
      }
    });
  }

  private updateDarkMode(mode: ThemeMode): void {
    let isDark: boolean;

    switch (mode) {
      case 'dark':
        isDark = true;
        break;
      case 'light':
        isDark = false;
        break;
      case 'auto':
      default:
        isDark = this.mediaQuery.matches;
        break;
    }

    this.darkModeSubject.next(isDark);
  }

  private updateHtmlTheme(isDark: boolean): void {
    const theme = isDark ? 'dark' : 'light';
    this.document.documentElement.setAttribute('data-bs-theme', theme);
  }

  isDarkMode(): boolean {
    return this.darkModeSubject.value;
  }

  getCurrentThemeMode(): ThemeMode {
    return this.themeModeSubject.value;
  }

  setThemeMode(mode: ThemeMode): void {
    this.themeModeSubject.next(mode);

    if (mode === 'auto') {
      // Clear localStorage for auto mode
      localStorage.removeItem('theme');
    } else {
      // Save the specific mode to localStorage
      localStorage.setItem('theme', mode);
    }

    this.updateDarkMode(mode);
  }

  // Legacy methods for backward compatibility
  setTheme(isDark: boolean): void {
    this.setThemeMode(isDark ? 'dark' : 'light');
  }

  toggleTheme(): void {
    const currentMode = this.getCurrentThemeMode();
    if (currentMode === 'auto') {
      // If in auto mode, toggle to the opposite of current system preference
      this.setThemeMode(this.isDarkMode() ? 'light' : 'dark');
    } else {
      // Toggle between dark and light
      this.setThemeMode(currentMode === 'dark' ? 'light' : 'dark');
    }
  }

  // Reset to system preference (auto mode)
  resetToSystemTheme(): void {
    this.setThemeMode('auto');
  }
}
